import { needsTokenRefresh } from "@/lib/shared/auth-utils";
import { create } from "zustand";
import { AuthStore, AuthUser, UserInfo } from "../types/auth";

// Refresh mutex to prevent concurrent refresh attempts
let refreshMutex = false;
let refreshInterval: NodeJS.Timeout | null = null;

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false,

  login: (user: AuthUser) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("auth-token", user.token);
      localStorage.setItem("refresh-token", user.refreshToken);
      if (user.userInfo) {
        localStorage.setItem("user-info", JSON.stringify(user.userInfo));
      }
    }
    set({ user, isAuthenticated: true });

    // Start token refresh monitoring
    get().startTokenRefreshMonitoring();
  },

  logout: async () => {
    // Stop token refresh monitoring
    get().stopTokenRefreshMonitoring();

    // Skip client-side operations during SSR
    if (typeof window === "undefined") {
      set({ user: null, isAuthenticated: false });
      return;
    }

    // TODO: Disconnect WebSocket when Go backend WebSocket is implemented
    // The WebSocket provider in providers/websocket-provider.tsx will handle this

    // Clear server session (optional - mainly client-side)
    const token = localStorage.getItem("auth-token");
    if (token) {
      try {
        await fetch("/api/v1/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.warn("Failed to notify server of logout:", error);
      }
    }

    // Clear localStorage
    localStorage.removeItem("auth-token");
    localStorage.removeItem("refresh-token");
    localStorage.removeItem("user-info");

    set({ user: null, isAuthenticated: false });
  },

  updateUserInfo: (userInfo: UserInfo) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("user-info", JSON.stringify(userInfo));
    }
    set((state) => ({
      user: state.user ? { ...state.user, userInfo } : null,
    }));
  },

  updateTokens: (tokens: { token: string; refreshToken: string }) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("auth-token", tokens.token);
      localStorage.setItem("refresh-token", tokens.refreshToken);
    }
    set((state) => ({
      user: state.user ? { ...state.user, ...tokens } : null,
    }));

    // TODO: Notify WebSocket about token update when Go backend WebSocket is implemented
    // The WebSocket provider in providers/websocket-provider.tsx will handle this
  },

  refreshToken: async (): Promise<boolean> => {
    // Prevent concurrent refresh attempts
    if (refreshMutex) {
      console.log("Token refresh already in progress, skipping...");
      return false;
    }

    refreshMutex = true;

    try {
      const currentRefreshToken =
        typeof window !== "undefined"
          ? localStorage.getItem("refresh-token")
          : null;

      if (!currentRefreshToken) {
        throw new Error("No refresh token available");
      }

      // Use new v2 refresh endpoint
      const response = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (response.ok) {
        const tokenData = await response.json();

        // Update tokens in store and localStorage (this will also notify WebSocket)
        get().updateTokens(tokenData);

        return true;
      } else {
        // Refresh failed, logout user
        await get().logout();
        return false;
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Refresh failed, logout user
      await get().logout();
      return false;
    } finally {
      refreshMutex = false;
    }
  },

  startTokenRefreshMonitoring: () => {
    if (typeof window === "undefined") return;

    console.log("[Auth Store] Starting token refresh monitoring");

    // Clear existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }

    // Check token every minute
    refreshInterval = setInterval(async () => {
      const state = get();
      if (!state.isAuthenticated || !state.user?.token) {
        return;
      }

      // Check if token needs refresh (5 minutes before expiry)
      if (needsTokenRefresh(state.user.token)) {
        console.log(
          "[Auth Store] Token needs refresh, refreshing proactively..."
        );
        await state.refreshToken();
      }
    }, 60 * 1000); // Check every minute
  },

  stopTokenRefreshMonitoring: () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
  },

  initializeAuth: () => {
    if (typeof window === "undefined") {
      return; // Skip on server-side
    }

    const token = localStorage.getItem("auth-token");
    const refreshToken = localStorage.getItem("refresh-token");
    const userInfoStr = localStorage.getItem("user-info");

    if (token && refreshToken) {
      const userInfo = userInfoStr ? JSON.parse(userInfoStr) : undefined;
      set({
        user: { token, refreshToken, userInfo },
        isAuthenticated: true,
        isInitialized: true,
      });

      // Start token refresh monitoring
      get().startTokenRefreshMonitoring();
    } else {
      set({ isInitialized: true });
    }
  },
}));
